// src/pages/LeadDetail.jsx — Complete Stacked Card Lead Detail Page
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { fmtDate, timeAgo } from '../utils/formatters';
import { LEAD_STATUSES } from '../utils/constants';
import StatusBadge from '../components/StatusBadge';
import DocumentUpload from '../components/DocumentUpload';
import { Modal, Form, Button } from 'react-bootstrap';
import toast from 'react-hot-toast';
import {
  ArrowLeft, User, Phone, Shield, Sliders, Tag,
  Edit3, Clock, Car, Lock, FileText, Activity,
  ChevronDown, ChevronUp, Plus, MessageSquare,
  X, Send, Sparkles, Bell, BellPlus, CheckCircle2, Trash2
} from 'lucide-react';
import './LeadDetail.css';
import AISummaryCard from '../components/ai/AISummaryCard';
import AIResponseModal from '../components/ai/AIResponseModal';
import AIStockMatcherCard from '../components/ai/AIStockMatcherCard';
import AIIntentBadge from '../components/ai/AIIntentBadge';
import SetReminderModal from '../components/SetReminderModal';
import { useReminders } from '../context/ReminderContext';

function copyText(text, label = 'Copied to clipboard!') {
  if (!text) return;
  navigator.clipboard?.writeText(text).then(() => toast.success(label, { duration: 1500 }));
}

function LeadDetailSkeleton() {
  return (
    <div className="ld-skeleton-container">
      <div className="ld-skeleton-topbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div className="ld-skeleton-pulse" style={{ width: 110, height: 32, borderRadius: 8 }} />
          <div className="ld-skeleton-pulse" style={{ width: 180, height: 26, borderRadius: 6 }} />
          <div className="ld-skeleton-pulse" style={{ width: 85, height: 22, borderRadius: 12 }} />
        </div>
        <div className="ld-skeleton-pulse" style={{ width: 100, height: 30, borderRadius: 20 }} />
      </div>

      <div className="ld-main-grid">
        <div className="ld-left-sidebar">
          {[1, 2, 3, 4, 5].map(n => (
            <div key={n} className="ld-skeleton-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <div className="ld-skeleton-pulse" style={{ width: '55%', height: 16, borderRadius: 4 }} />
                <div className="ld-skeleton-pulse" style={{ width: 16, height: 16, borderRadius: 4 }} />
              </div>
              <div className="ld-skeleton-pulse" style={{ width: '100%', height: 32, borderRadius: 6 }} />
              <div className="ld-skeleton-pulse" style={{ width: '75%', height: 14, borderRadius: 4 }} />
              <div className="ld-skeleton-pulse" style={{ width: '85%', height: 14, borderRadius: 4 }} />
            </div>
          ))}
        </div>

        <div className="ld-right-feed">
          {[1, 2, 3, 4].map(n => (
            <div key={n} className="ld-skeleton-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div className="ld-skeleton-pulse" style={{ width: '40%', height: 16, borderRadius: 4 }} />
                <div className="ld-skeleton-pulse" style={{ width: 18, height: 18, borderRadius: '50%' }} />
              </div>
              <div className="ld-skeleton-pulse" style={{ width: '100%', height: 75, borderRadius: 8 }} />
              <div className="ld-skeleton-pulse" style={{ width: 110, height: 30, borderRadius: 6, marginLeft: 'auto' }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [lead, setLead] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [brands, setBrands] = useState([]);
  const [availableCars, setAvailableCars] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Editability toggle
  const [editable, setEditable] = useState(true);

  // Collapsible section states (false = expanded, true = collapsed)
  const [collapsed, setCollapsed] = useState({});

  const toggleSection = (sectionKey) => {
    setCollapsed(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  // Follow-up Note composer state
  const [newNote, setNewNote] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [submittingNote, setSubmittingNote] = useState(false);
  const [draftMessage, setDraftMessage] = useState('');
  const [suggestingAI, setSuggestingAI] = useState(false);
  const [editingRemarks, setEditingRemarks] = useState(false);
  const [remarksText, setRemarksText] = useState('');

  // Tags state
  const [newTagInput, setNewTagInput] = useState('');

  // Private notes state
  const [privateNotesText, setPrivateNotesText] = useState('');

  // Add Bought Car Modal state
  const [showAddCarModal, setShowAddCarModal] = useState(false);
  const [selectedStockCarId, setSelectedStockCarId] = useState('');
  const [customCar, setCustomCar] = useState({ make: '', model: '', year: new Date().getFullYear(), chassis: '', soldPrice: '' });
  const [carModalTab, setCarModalTab] = useState('inventory'); // 'inventory' | 'custom'
  // AI Response Modal state
  const [showAIResponseModal, setShowAIResponseModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const { reminders, completeReminder, snoozeReminder, deleteReminder } = useReminders();
  const leadReminders = useMemo(() => {
    return reminders.filter(r => String(r.leadId) === String(id));
  }, [reminders, id]);

  // ── Load Lead & Related Data ─────────────────────────────────
  const fetchLeadData = useCallback(async () => {
    try {
      setLoading(true);
      const [leadRes, empRes, brandRes, carRes] = await Promise.all([
        api.get(`/leads/${id}`),
        api.get('/employees').catch(() => ({ data: [] })),
        api.get('/brands').catch(() => ({ data: [] })),
        api.get('/cars', { params: { limit: 50 } }).catch(() => ({ data: [] }))
      ]);

      const data = leadRes.data;
      setLead(data);
      setRemarksText(data.remarks || '');
      setPrivateNotesText(data.privateNotes || '');
      setEmployees(Array.isArray(empRes.data) ? empRes.data : []);
      setBrands(Array.isArray(brandRes.data) ? brandRes.data : []);
      setAvailableCars(Array.isArray(carRes.data) ? carRes.data : (carRes.data.cars || []));
    } catch (err) {
      console.error('Error loading lead detail:', err);
      toast.error('Failed to load lead details');
      navigate('/dashboard/leads');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/leads/${id}/history`);
      setHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      setHistory([]);
    }
  }, [id]);

  useEffect(() => {
    fetchLeadData();
    fetchHistory();
  }, [fetchLeadData, fetchHistory]);

  // Keyboard shortcut Esc to return to leads list
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') navigate('/dashboard/leads');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  // ── Handlers ──────────────────────────────────────────────────
  const updateField = async (field, value) => {
    try {
      const { data } = await api.put(`/leads/${id}`, { [field]: value });
      setLead(data);
      if (field === 'remarks') {
        setRemarksText(data.remarks || '');
      }
      toast.success('Updated successfully', { duration: 1500 });
      fetchLeadData();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update lead');
    }
  };

  const submitFollowUpNote = async () => {
    if (!newNote.trim()) return;
    setSubmittingNote(true);
    try {
      const payload = { note: newNote.trim() };
      if (followUpDate) payload.followUpDate = followUpDate;
      const { data } = await api.post(`/leads/${id}/followups`, payload);
      setLead(data);
      setNewNote('');
      setFollowUpDate('');
      toast.success('Follow-up note added!');
      fetchHistory();
    } catch (err) {
      toast.error('Failed to add note');
    } finally {
      setSubmittingNote(false);
    }
  };

  const handleAddTag = async () => {
    if (!newTagInput.trim() || !lead) return;
    const tag = newTagInput.trim();
    const currentTags = Array.isArray(lead.tags) ? lead.tags : [];
    if (currentTags.includes(tag)) {
      setNewTagInput('');
      return;
    }
    const updated = [...currentTags, tag];
    await updateField('tags', updated);
    setNewTagInput('');
  };

  const handleRemoveTag = async (tagToRemove) => {
    if (!lead) return;
    const currentTags = Array.isArray(lead.tags) ? lead.tags : [];
    const updated = currentTags.filter(t => t !== tagToRemove);
    await updateField('tags', updated);
  };

  const handleSavePrivateNotes = async () => {
    if (lead && privateNotesText !== lead.privateNotes) {
      await updateField('privateNotes', privateNotesText);
    }
  };

  const handleAddBoughtCar = async () => {
    try {
      if (carModalTab === 'inventory') {
        if (!selectedStockCarId) return;
        await api.put(`/cars/${selectedStockCarId}`, { status: 'Sold', buyerLeadId: id, isSold: 1 });
        toast.success('Car linked to lead!');
      } else {
        if (!customCar.make || !customCar.model) {
          toast.error('Make and Model are required');
          return;
        }
        await api.post(`/cars`, { 
          make: customCar.make, 
          model: customCar.model, 
          year: customCar.year || new Date().getFullYear(), 
          chassis: customCar.chassis, 
          cifPrice: customCar.soldPrice, 
          status: 'Sold', 
          buyerLeadId: id, 
          isSold: 1 
        });
        toast.success('Custom car created and linked!');
      }
      
      const { data } = await api.get(`/leads/${id}`);
      setLead(data);
      
      setShowAddCarModal(false);
      setSelectedStockCarId('');
      setCustomCar({ make: '', model: '', year: new Date().getFullYear(), chassis: '', soldPrice: '' });
      fetchHistory();
    } catch (err) {
      toast.error('Failed to add bought car');
    }
  };

  if (loading || !lead) {
    return <LeadDetailSkeleton />;
  }

  const rawPhone = String(lead.number || '').trim();
  const cleanPhone = rawPhone.replace(/[^0-9]/g, '');
  const assignedAgent = employees.find(e => String(e.id || e._id) === String(lead.agentId));
  const assignedManager = employees.find(e => String(e.id || e._id) === String(lead.managerId || assignedAgent?.reportingTo));
  const brandObj = brands.find(b => String(b.id || b._id) === String(lead.brandId));

  const followUpList = Array.isArray(lead.followUpHistory) ? lead.followUpHistory : (lead.followUps || []);
  const documentsList = Array.isArray(lead.documents) ? lead.documents : [];
  const boughtCarsList = Array.isArray(lead.boughtCars) ? lead.boughtCars : [];
  const tagsList = Array.isArray(lead.tags) ? lead.tags : [];

  return (
    <div className="lead-detail-page">
      {/* ── Top Bar / Header ── */}
      <div className="ld-topbar-header">
        <div className="ld-topbar-left">
          <button className="btn-back-leads" onClick={() => navigate('/dashboard/leads')}>
            <ArrowLeft size={16} /> Back to Leads
          </button>
          <div className="ld-title-wrap flex items-center gap-3">
            <h1 className="ld-lead-title">{lead.name}</h1>
            <span className="ld-status-pill">
              <StatusBadge status={lead.status} />
            </span>
            <AIIntentBadge
              leadId={lead.id}
              score={lead.intentScore}
              reason={lead.intentReason}
              onScored={(score, reason) => setLead(prev => ({ ...prev, intentScore: score, intentReason: reason }))}
            />
          </div>
        </div>

        <div className="ld-topbar-right" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={() => setShowAIResponseModal(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              borderRadius: '8px',
              background: 'rgba(16, 185, 129, 0.15)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              color: '#10b981',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            <Sparkles size={14} />
            <span>AI Reply Generator</span>
          </button>
          <button
            onClick={() => setShowReminderModal(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              borderRadius: '8px',
              background: 'var(--primary-light)',
              border: '1px solid var(--primary-border)',
              color: 'var(--primary)',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            <BellPlus size={14} />
            <span>Set Reminder</span>
            {leadReminders.filter(r => !r.isCompleted).length > 0 && (
              <span className="badge rounded-pill bg-danger ms-1" style={{ fontSize: 10 }}>
                {leadReminders.filter(r => !r.isCompleted).length}
              </span>
            )}
          </button>
          <button
            className={`btn-editable-toggle ${editable ? 'active' : ''}`}
            onClick={() => setEditable(prev => !prev)}
            title="Toggle Editability"
          >
            {editable ? (
              <span className="d-inline-flex align-items-center gap-1"><Edit3 size={12} /> Editable</span>
            ) : (
              <span className="d-inline-flex align-items-center gap-1"><Lock size={12} /> Read Only</span>
            )}
          </button>
        </div>
      </div>

      {/* ── Main Two-Column Layout ── */}
      <div className="ld-main-grid">

        {/* ════════════════════════════════════════════════════════
            LEFT SIDEBAR (Stacked Collapsible Cards)
        ════════════════════════════════════════════════════════ */}
        <div className="ld-left-sidebar">

          {/* 1. BASIC INFORMATION CARD */}
          <div className="ld-card">
            <div className="ld-card-header" onClick={() => toggleSection('basic')}>
              <div className="ld-card-header-left">
                <User size={16} className="ld-card-icon" />
                <span className="ld-card-title">BASIC INFORMATION</span>
              </div>
              <button className="ld-collapse-btn">
                {collapsed.basic ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              </button>
            </div>

            {!collapsed.basic && (
              <div className="ld-card-body">
                <div className="ld-field-group">
                  <label className="ld-field-label">FULL NAME</label>
                  {editable ? (
                    <input
                      type="text"
                      className="ld-input-text"
                      value={lead.name || ''}
                      onChange={(e) => setLead({ ...lead, name: e.target.value })}
                      onBlur={(e) => updateField('name', e.target.value)}
                    />
                  ) : (
                    <div className="ld-field-value bold">{lead.name || '—'}</div>
                  )}
                </div>

                <div className="ld-field-group">
                  <label className="ld-field-label">LEAD DATE</label>
                  <div className="ld-field-value mono">
                    {lead.createDate || lead.createdAt ? fmtDate(lead.createDate || lead.createdAt) : '—'}
                  </div>
                </div>

                <div className="ld-field-group">
                  <label className="ld-field-label">SOURCE</label>
                  {editable ? (
                    <input
                      type="text"
                      className="ld-input-text"
                      value={lead.source || ''}
                      onChange={(e) => setLead({ ...lead, source: e.target.value })}
                      onBlur={(e) => updateField('source', e.target.value)}
                    />
                  ) : (
                    <div className="ld-field-value">{lead.source || 'Facebook Ads'}</div>
                  )}
                </div>

                <div className="ld-field-group">
                  <label className="ld-field-label">EST. VALUE</label>
                  {editable ? (
                    <input
                      type="text"
                      className="ld-input-text"
                      value={lead.estimatedValue || ''}
                      placeholder="PKR 0.00"
                      onChange={(e) => setLead({ ...lead, estimatedValue: e.target.value })}
                      onBlur={(e) => updateField('estimatedValue', e.target.value)}
                    />
                  ) : (
                    <div className="ld-field-value">{lead.estimatedValue || 'PKR 0.00'}</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* 2. CONTACT DETAILS CARD */}
          <div className="ld-card">
            <div className="ld-card-header" onClick={() => toggleSection('contact')}>
              <div className="ld-card-header-left">
                <Phone size={16} className="ld-card-icon" />
                <span className="ld-card-title">CONTACT DETAILS</span>
              </div>
              <button className="ld-collapse-btn">
                {collapsed.contact ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              </button>
            </div>

            {!collapsed.contact && (
              <div className="ld-card-body">
                <div className="ld-field-group">
                  <label className="ld-field-label">PHONE</label>
                  <div className="ld-phone-row" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {editable ? (
                      <input
                        type="text"
                        className="ld-input-text font-monospace"
                        style={{ flex: 1 }}
                        value={lead.number || ''}
                        onChange={(e) => setLead({ ...lead, number: e.target.value })}
                        onBlur={(e) => updateField('number', e.target.value)}
                        placeholder="Enter phone number"
                      />
                    ) : (
                      <span className="ld-phone-mono" style={{ flex: 1 }} onClick={() => copyText(rawPhone, 'Copied phone number!')}>
                        {rawPhone || 'No phone'}
                      </span>
                    )}
                    {cleanPhone && (
                      <button
                        className="btn-whatsapp-action"
                        style={{ flexShrink: 0 }}
                        onClick={() => window.open(`https://wa.me/${cleanPhone}`, '_blank')}
                      >
                        <MessageSquare size={13} /> WhatsApp
                      </button>
                    )}
                  </div>
                </div>

                <div className="ld-field-group">
                  <label className="ld-field-label">EMAIL</label>
                  {editable ? (
                    <input
                      type="email"
                      className="ld-input-text"
                      value={lead.email || ''}
                      onChange={(e) => setLead({ ...lead, email: e.target.value })}
                      onBlur={(e) => updateField('email', e.target.value)}
                    />
                  ) : (
                    <div className="ld-field-value mailto" onClick={() => copyText(lead.email, 'Copied email!')}>
                      {lead.email || 'divoncadore@gmail.com'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* 3. ASSIGNMENT CARD */}
          <div className="ld-card">
            <div className="ld-card-header" onClick={() => toggleSection('assignment')}>
              <div className="ld-card-header-left">
                <Shield size={16} className="ld-card-icon" />
                <span className="ld-card-title">ASSIGNMENT</span>
              </div>
              <button className="ld-collapse-btn">
                {collapsed.assignment ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              </button>
            </div>

            {!collapsed.assignment && (
              <div className="ld-card-body">
                <div className="ld-field-group">
                  <label className="ld-field-label">ASSIGNED AGENT</label>
                  <select
                    className="ld-select"
                    value={lead.agentId || ''}
                    disabled={!editable}
                    onChange={(e) => updateField('agentId', e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">No Agent</option>
                    {employees.map(emp => (
                      <option key={emp.id || emp._id} value={emp.id || emp._id}>
                        {emp.name} ({emp.role || 'Staff'})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="ld-field-group">
                  <label className="ld-field-label">LEAD STATUS</label>
                  <div className="ld-field-value">{lead.status || 'Customer'}</div>
                </div>

                <div className="ld-field-group">
                  <label className="ld-field-label">BRAND</label>
                  <div className="ld-field-value">{brandObj ? brandObj.name : (lead.brandId || '3')}</div>
                </div>

                <div className="ld-field-group">
                  <label className="ld-field-label">ASSIGNED MANAGER</label>
                  <div className="ld-field-value">{assignedManager ? assignedManager.name : '—'}</div>
                </div>

                <div className="ld-field-group">
                  <label className="ld-field-label">ASSIGNED ON</label>
                  <div className="ld-field-value">{lead.assignedAt ? fmtDate(lead.assignedAt) : '—'}</div>
                </div>

                <div className="ld-field-group">
                  <label className="ld-field-label">PROTECTED UNTIL</label>
                  <div className="ld-field-value">{lead.protectedUntil ? fmtDate(lead.protectedUntil) : '—'}</div>
                </div>
              </div>
            )}
          </div>

          {/* 4. STATUS CARD */}
          <div className="ld-card">
            <div className="ld-card-header" onClick={() => toggleSection('status')}>
              <div className="ld-card-header-left">
                <Sliders size={16} className="ld-card-icon" />
                <span className="ld-card-title">STATUS</span>
              </div>
              <button className="ld-collapse-btn">
                {collapsed.status ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              </button>
            </div>

            {!collapsed.status && (
              <div className="ld-card-body">
                <div className="ld-field-group">
                  <label className="ld-field-label">CURRENT STATUS</label>
                  <select
                    className="ld-select ld-select-status"
                    value={lead.status || 'Customer'}
                    disabled={!editable}
                    onChange={(e) => updateField('status', e.target.value)}
                  >
                    {LEAD_STATUSES.map(st => (
                      <option key={st} value={st}>{st}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* 5. TAGS CARD */}
          <div className="ld-card">
            <div className="ld-card-header" onClick={() => toggleSection('tags')}>
              <div className="ld-card-header-left">
                <Tag size={16} className="ld-card-icon" />
                <span className="ld-card-title">TAGS</span>
                <span className="ld-badge-count">{tagsList.length}</span>
              </div>
              <button className="ld-collapse-btn">
                {collapsed.tags ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              </button>
            </div>

            {!collapsed.tags && (
              <div className="ld-card-body">
                <div className="ld-tags-container">
                  {tagsList.map(tag => (
                    <span key={tag} className="ld-tag-pill">
                      {tag}
                      {editable && (
                        <button className="ld-tag-remove" onClick={() => handleRemoveTag(tag)}>
                          <X size={12} />
                        </button>
                      )}
                    </span>
                  ))}
                </div>

                {editable && (
                  <div className="ld-add-tag-row">
                    <input
                      type="text"
                      className="ld-input-text ld-input-tag"
                      placeholder="Add tag..."
                      value={newTagInput}
                      onChange={(e) => setNewTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddTag();
                        }
                      }}
                    />
                    <button className="btn-add-tag-icon" onClick={handleAddTag}>
                      <Plus size={16} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

        </div>


        {/* ════════════════════════════════════════════════════════
            RIGHT MAIN FEED (Stacked Collapsible Cards)
        ════════════════════════════════════════════════════════ */}
        <div className="ld-right-feed">

          {/* AI Executive Summary Card */}
          <AISummaryCard
            lead={lead}
            onSummaryUpdated={(s) => setLead(prev => ({ ...prev, aiSummary: s }))}
          />

          {/* AI Stock Matcher Card */}
          <AIStockMatcherCard lead={lead} />

          {/* LEAD REMINDERS & SCHEDULED ALERTS CARD */}
          <div className="ld-card">
            <div className="ld-card-header" onClick={() => toggleSection('reminders')}>
              <div className="ld-card-header-left">
                <Bell size={16} className="ld-card-icon" style={{ color: 'var(--primary)' }} />
                <span className="ld-card-title">REMINDERS & SCHEDULED ALERTS</span>
                {leadReminders.filter(r => !r.isCompleted).length > 0 && (
                  <span className="badge rounded-pill bg-danger" style={{ fontSize: 10, padding: '3px 7px' }}>
                    {leadReminders.filter(r => !r.isCompleted).length}
                  </span>
                )}
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary py-0 px-2"
                  style={{ fontSize: 11, borderRadius: 6, height: 24, display: 'flex', alignItems: 'center', gap: 4 }}
                  onClick={(e) => { e.stopPropagation(); setShowReminderModal(true); }}
                >
                  <Plus size={12} /> Add
                </button>
                <button className="ld-collapse-btn">
                  {collapsed.reminders ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                </button>
              </div>
            </div>

            {!collapsed.reminders && (
              <div className="ld-card-body p-3">
                {leadReminders.length === 0 ? (
                  <div className="text-center text-muted py-3" style={{ fontSize: 12.5 }}>
                    <Clock size={20} style={{ opacity: 0.4, marginBottom: 4 }} />
                    <div>No reminders set for this lead.</div>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 mt-1"
                      style={{ fontSize: 12, textDecoration: 'none' }}
                      onClick={() => setShowReminderModal(true)}
                    >
                      + Schedule a reminder
                    </button>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {leadReminders.map(r => {
                      const isDue = new Date(r.remindAt) <= new Date() && !r.isCompleted;
                      return (
                        <div
                          key={r.id}
                          className="p-2 rounded d-flex align-items-center justify-content-between gap-2"
                          style={{
                            background: isDue ? 'rgba(255, 159, 10, 0.08)' : 'var(--surface-2)',
                            border: `1px solid ${isDue ? 'var(--warning)' : 'var(--border)'}`,
                            opacity: r.isCompleted ? 0.6 : 1
                          }}
                        >
                          <div className="d-flex align-items-center gap-2 overflow-hidden flex-1">
                            <button
                              type="button"
                              className="btn btn-sm p-0 border-0 bg-transparent"
                              title={r.isCompleted ? "Mark incomplete" : "Mark done"}
                              onClick={() => completeReminder(r.id, !r.isCompleted)}
                              style={{ color: r.isCompleted ? 'var(--success)' : 'var(--muted)', cursor: 'pointer' }}
                            >
                              {r.isCompleted ? <CheckCircle2 size={16} /> : <div style={{ width: 14, height: 14, borderRadius: 4, border: '1.5px solid var(--muted)' }} />}
                            </button>
                            <div className="overflow-hidden">
                              <div className="fw-semibold text-truncate" style={{ fontSize: 12.5, textDecoration: r.isCompleted ? 'line-through' : 'none' }}>
                                {r.title}
                              </div>
                              <div className="small text-muted" style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
                                <Clock size={10} className="me-1" style={{ verticalAlign: -1 }} />
                                {new Date(r.remindAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                {isDue && <span className="ms-1 fw-bold text-warning">(Due Now)</span>}
                              </div>
                            </div>
                          </div>

                          <div className="d-flex align-items-center gap-1">
                            {!r.isCompleted && (
                              <button
                                type="button"
                                className="btn btn-sm py-0 px-1 text-muted"
                                title="Postpone 15m"
                                style={{ fontSize: 11, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 5 }}
                                onClick={() => snoozeReminder(r.id, 15)}
                              >
                                +15m
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn btn-sm p-1 text-muted"
                              title="Delete reminder"
                              style={{ border: 'none', background: 'transparent' }}
                              onClick={() => deleteReminder(r.id)}
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* CURRENT REMARKS & SUMMARY CARD */}
          <div className="ld-card">
            <div className="ld-card-header" onClick={() => toggleSection('currentRemarks')}>
              <div className="ld-card-header-left">
                <MessageSquare size={16} className="ld-card-icon" />
                <span className="ld-card-title">CURRENT REMARKS & SUMMARY</span>
                {lead.remarksDate && (
                  <span className="text-muted small" style={{ fontSize: '11px', fontWeight: 500, marginLeft: '6px' }}>
                    Updated {fmtDate(lead.remarksDate)}
                  </span>
                )}
              </div>
              <button className="ld-collapse-btn">
                {collapsed.currentRemarks ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              </button>
            </div>

            {!collapsed.currentRemarks && (
              <div className="ld-card-body">
                {editingRemarks ? (
                  <div>
                    <textarea
                      className="ld-textarea"
                      rows={3}
                      value={remarksText}
                      onChange={(e) => setRemarksText(e.target.value)}
                      placeholder="Enter remarks..."
                    />
                    <div className="d-flex justify-content-end gap-2 mt-2">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => {
                          setRemarksText(lead.remarks || '');
                          setEditingRemarks(false);
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={async () => {
                          await updateField('remarks', remarksText.trim());
                          setEditingRemarks(false);
                        }}
                      >
                        Save Remarks
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="d-flex justify-content-between align-items-start gap-3">
                    <div style={{ flex: 1, fontSize: '13.5px', color: lead.remarks ? 'var(--text)' : 'var(--muted)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                      {lead.remarks || 'No remarks recorded yet. Click Edit to add remarks.'}
                    </div>
                    {editable && (
                      <button
                        className="btn btn-sm p-1 text-primary d-flex align-items-center gap-1"
                        style={{ border: '1px solid var(--border)', background: 'var(--surface-2)', borderRadius: '6px', fontSize: '11.5px', padding: '3px 8px' }}
                        onClick={() => {
                          setRemarksText(lead.remarks || '');
                          setEditingRemarks(true);
                        }}
                      >
                        <Edit3 size={12} /> Edit Remarks
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 1. ADD FOLLOW-UP NOTE CARD */}
          <div className="ld-card">
            <div className="ld-card-header" onClick={() => toggleSection('addNote')}>
              <div className="ld-card-header-left">
                <Edit3 size={16} className="ld-card-icon" />
                <span className="ld-card-title">ADD FOLLOW-UP NOTE</span>
              </div>
              <button className="ld-collapse-btn">
                {collapsed.addNote ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              </button>
            </div>

            {!collapsed.addNote && (
              <div className="ld-card-body">
                <textarea
                  className="ld-textarea"
                  rows={3}
                  placeholder="Add a follow-up note... (replaces remarks and logs to history)"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') submitFollowUpNote();
                  }}
                />
                <div className="ld-composer-actions">
                  <div className="ld-date-picker-wrap">
                    <span className="ld-date-label">Follow-up date:</span>
                    <input
                      type="datetime-local"
                      className="ld-input-date"
                      value={followUpDate}
                      onChange={(e) => setFollowUpDate(e.target.value)}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      className="btn-add-note-primary"
                      style={{ background: 'var(--success)', border: 'none' }}
                      disabled={suggestingAI}
                      onClick={async () => {
                        setSuggestingAI(true);
                        try {
                          const { data } = await api.post('/ai/suggest-action', {
                            currentRemarks: lead?.remarks,
                            newNote: newNote,
                            leadName: lead?.name
                          });
                          if (data.date) setFollowUpDate(data.date);
                          if (data.message) setDraftMessage(data.message);
                          toast.success('AI Suggestions generated!');
                        } catch (err) {
                          toast.error('AI suggestion failed');
                        } finally {
                          setSuggestingAI(false);
                        }
                      }}
                    >
                      {suggestingAI ? (
                        <span className="d-inline-flex align-items-center gap-1"><Sparkles size={13} className="text-primary" /> Thinking...</span>
                      ) : (
                        <span className="d-inline-flex align-items-center gap-1"><Sparkles size={13} className="text-primary" /> AI Suggest</span>
                      )}
                    </button>
                    <button
                      className="btn-add-note-primary"
                      disabled={submittingNote || !newNote.trim()}
                      onClick={submitFollowUpNote}
                    >
                      <Send size={14} /> Add Note
                    </button>
                  </div>
                </div>
                {draftMessage && (
                  <div style={{ marginTop: '12px', background: 'var(--surface-2)', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-2)' }}>Draft WhatsApp Message</span>
                      <button 
                        style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', background: 'var(--success-bg)', color: 'var(--success)', border: 'none', cursor: 'pointer' }}
                        onClick={() => {
                          window.open(`https://wa.me/${lead.number.replace(/\D/g, '')}?text=${encodeURIComponent(draftMessage)}`, '_blank');
                        }}
                      >
                        Open WhatsApp
                      </button>
                    </div>
                    <textarea
                      className="ld-textarea"
                      rows={2}
                      style={{ background: 'var(--surface)', fontSize: '13px' }}
                      value={draftMessage}
                      onChange={(e) => setDraftMessage(e.target.value)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 2. FOLLOW-UP HISTORY CARD */}
          <div className="ld-card">
            <div className="ld-card-header" onClick={() => toggleSection('history')}>
              <div className="ld-card-header-left">
                <Clock size={16} className="ld-card-icon" />
                <span className="ld-card-title">FOLLOW-UP HISTORY</span>
                <span className="ld-badge-count">{followUpList.length}</span>
              </div>
              <button className="ld-collapse-btn">
                {collapsed.history ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              </button>
            </div>

            {!collapsed.history && (
              <div className="ld-card-body">
                {followUpList.length === 0 ? (
                  <div className="ld-empty-text">No follow-up notes recorded yet.</div>
                ) : (
                  <div className="ld-history-list">
                    {followUpList.map((item, idx) => (
                      <div key={idx} className="ld-history-item">
                        <div className="ld-history-meta">
                          <span className="ld-history-author">{item.createdByName || item.actor || 'Agent'}</span>
                          <span className="ld-history-date">{fmtDate(item.createdAt || item.date)}</span>
                        </div>
                        <div className="ld-history-text">{item.note || item.action}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 3. BOUGHT CARS CARD */}
          <div className="ld-card">
            <div className="ld-card-header" onClick={() => toggleSection('cars')}>
              <div className="ld-card-header-left">
                <Car size={16} className="ld-card-icon" />
                <span className="ld-card-title">BOUGHT CARS</span>
                <span className="ld-badge-count">{boughtCarsList.length}</span>
              </div>
              <button className="ld-collapse-btn">
                {collapsed.cars ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              </button>
            </div>

            {!collapsed.cars && (
              <div className="ld-card-body text-center">
                {boughtCarsList.length === 0 ? (
                  <div className="ld-empty-text py-3">No cars bought by this customer yet.</div>
                ) : (
                  <div className="ld-bought-cars-grid mb-3">
                    {boughtCarsList.map((c, idx) => (
                      <div key={idx} className="ld-bought-car-item">
                        <div className="ld-car-title">{c.year} {c.make} {c.model}</div>
                        <div className="ld-car-sub font-mono">Chassis: {c.chassis || 'N/A'} | Price: PKR {Number(c.soldPrice || c.price || 0).toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                )}

                {editable && (
                  <button className="btn-add-bought-car" onClick={() => setShowAddCarModal(true)}>
                    + Add Bought Car
                  </button>
                )}
              </div>
            )}
          </div>

          {/* 4. PRIVATE NOTES CARD */}
          <div className="ld-card">
            <div className="ld-card-header" onClick={() => toggleSection('privateNotes')}>
              <div className="ld-card-header-left">
                <Lock size={16} className="ld-card-icon" />
                <span className="ld-card-title">PRIVATE NOTES</span>
              </div>
              <button className="ld-collapse-btn">
                {collapsed.privateNotes ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              </button>
            </div>

            {!collapsed.privateNotes && (
              <div className="ld-card-body">
                <div className="ld-private-note-label">NOTES (VISIBLE ONLY TO YOU AND ADMIN)</div>
                <textarea
                  className="ld-textarea ld-textarea-private"
                  rows={2}
                  value={privateNotesText}
                  placeholder="Private internal notes..."
                  disabled={!editable}
                  onChange={(e) => setPrivateNotesText(e.target.value)}
                  onBlur={handleSavePrivateNotes}
                />
              </div>
            )}
          </div>

          {/* 5. DOCUMENTS CARD */}
          <div className="ld-card">
            <div className="ld-card-header" onClick={() => toggleSection('documents')}>
              <div className="ld-card-header-left">
                <FileText size={16} className="ld-card-icon" />
                <span className="ld-card-title">DOCUMENTS</span>
                <span className="ld-badge-count">{documentsList.length}</span>
              </div>
              <button className="ld-collapse-btn">
                {collapsed.documents ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              </button>
            </div>

            {!collapsed.documents && (
              <div className="ld-card-body">
                <DocumentUpload leadId={id} canEdit={editable} />
              </div>
            )}
          </div>

          {/* 6. ACTIVITY HISTORY CARD */}
          <div className="ld-card">
            <div className="ld-card-header" onClick={() => toggleSection('activity')}>
              <div className="ld-card-header-left">
                <Activity size={16} className="ld-card-icon" />
                <span className="ld-card-title">ACTIVITY HISTORY</span>
              </div>
              <button className="ld-collapse-btn">
                {collapsed.activity ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              </button>
            </div>

            {!collapsed.activity && (
              <div className="ld-card-body">
                {history.length === 0 ? (
                  <div className="ld-empty-text">No activity history recorded.</div>
                ) : (
                  <div className="ld-activity-timeline">
                    {history.map((act, idx) => (
                      <div key={idx} className="ld-activity-row">
                        <div className="ld-activity-dot" />
                        <div className="ld-activity-content">
                          <div className="ld-activity-header">
                            <span className="ld-activity-user">{act.performedBy || 'System'}</span>
                            <span className="ld-activity-time">{act.createdAt ? timeAgo(act.createdAt) : ''}</span>
                          </div>
                          <div className="ld-activity-desc">
                            {act.action === 'STATUS_CHANGED' ? `Changed status to "${act.newValue}"` :
                             act.action === 'CALL_LOGGED' ? `Logged a call: ${act.newValue || ''}` :
                             act.action === 'ASSIGNMENT_CHANGED' ? `Assigned to ${act.newValue}` :
                             act.action === 'FOLLOWUP_ADDED' ? `Added follow-up: ${act.newValue}` :
                             act.action === 'REMARKS_UPDATED' ? `Updated remarks: "${act.newValue}"` :
                             act.action === 'TAGS_UPDATED' ? `Updated tags to: ${act.newValue}` :
                             act.action || `Changed ${act.field} from "${act.oldValue || ''}" to "${act.newValue || ''}"`}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

        </div>

      </div>

      {/* ── Add Bought Car Modal ── */}
      <Modal show={showAddCarModal} onHide={() => setShowAddCarModal(false)} centered contentClassName="border-0" style={{ '--bs-modal-bg': 'var(--surface)', '--bs-modal-color': 'var(--text)' }}>
        <Modal.Header closeButton className="border-bottom" style={{ borderColor: 'var(--border) !important' }}>
          <Modal.Title style={{ fontSize: '18px', fontWeight: 600 }}>Add Bought Car for {lead.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex gap-2 mb-4 p-1 rounded" style={{ background: 'var(--surface-2)' }}>
            <Button
              variant={carModalTab === 'inventory' ? 'primary' : 'link'}
              className="flex-fill text-decoration-none border-0 fw-bold"
              style={{ color: carModalTab === 'inventory' ? '#fff' : 'var(--muted)', borderRadius: '6px' }}
              onClick={() => setCarModalTab('inventory')}
            >
              Stock Inventory
            </Button>
            <Button
              variant={carModalTab === 'custom' ? 'primary' : 'link'}
              className="flex-fill text-decoration-none border-0 fw-bold"
              style={{ color: carModalTab === 'custom' ? '#fff' : 'var(--muted)', borderRadius: '6px' }}
              onClick={() => setCarModalTab('custom')}
            >
              Custom Details
            </Button>
          </div>

          {carModalTab === 'inventory' ? (
            <Form.Group>
              <Form.Label style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text)' }}>Available Stock Car</Form.Label>
              <Form.Select
                value={selectedStockCarId}
                onChange={(e) => setSelectedStockCarId(e.target.value)}
                style={{ background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '8px', padding: '10px 14px' }}
              >
                <option value="">Choose a vehicle from stock...</option>
                {availableCars.map(c => (
                  <option key={c.id || c._id} value={c.id || c._id}>
                    {c.year} {c.make} {c.model} (Chassis: {c.chassis || 'N/A'})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          ) : (
            <div className="d-flex flex-column gap-3">
              <Form.Group>
                <Form.Label style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text)' }}>Make *</Form.Label>
                <Form.Control
                  placeholder="e.g. Toyota"
                  value={customCar.make}
                  onChange={e => setCustomCar({ ...customCar, make: e.target.value })}
                  style={{ background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)' }}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text)' }}>Model *</Form.Label>
                <Form.Control
                  placeholder="e.g. Corolla"
                  value={customCar.model}
                  onChange={e => setCustomCar({ ...customCar, model: e.target.value })}
                  style={{ background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)' }}
                />
              </Form.Group>
              <div className="d-flex gap-3">
                <Form.Group className="flex-fill">
                  <Form.Label style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text)' }}>Year</Form.Label>
                  <Form.Control
                    type="number"
                    value={customCar.year}
                    onChange={e => setCustomCar({ ...customCar, year: e.target.value })}
                    style={{ background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)' }}
                  />
                </Form.Group>
                <Form.Group className="flex-fill">
                  <Form.Label style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text)' }}>Sold Price</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Optional"
                    value={customCar.soldPrice}
                    onChange={e => setCustomCar({ ...customCar, soldPrice: e.target.value })}
                    style={{ background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)' }}
                  />
                </Form.Group>
              </div>
              <Form.Group>
                <Form.Label style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text)' }}>Chassis / VIN</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Chassis Number"
                  value={customCar.chassis}
                  onChange={(e) => setCustomCar({ ...customCar, chassis: e.target.value })}
                  style={{ background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)' }}
                />
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-top" style={{ borderColor: 'var(--border) !important' }}>
          <Button variant="link" className="text-decoration-none text-muted fw-bold" onClick={() => setShowAddCarModal(false)}>Cancel</Button>
          <Button variant="primary" className="fw-bold px-4" onClick={handleAddBoughtCar}>Save Car</Button>
        </Modal.Footer>
      </Modal>

      {/* AI Response Modal */}
      <AIResponseModal
        lead={lead}
        isOpen={showAIResponseModal}
        onClose={() => setShowAIResponseModal(false)}
      />

      {/* Set Reminder Modal */}
      <SetReminderModal
        show={showReminderModal}
        onClose={() => setShowReminderModal(false)}
        lead={lead}
      />

    </div>
  );
}